import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import NewsCard from "@/components/feature/NewsCard";
import WorkCard from "@/components/feature/WorkCard";
import CharacterCard from "@/components/feature/CharacterCard";
import { useSeo } from "@/hooks/useSeo";
import { newsList } from "@/mocks/news";
import { worksList } from "@/mocks/works";
import { charactersList } from "@/mocks/characters";

type TabKey = "all" | "news" | "works" | "characters";

const tabs: Array<{ key: TabKey; label: string }> = [
  { key: "all", label: "全部" },
  { key: "news", label: "资讯" },
  { key: "works", label: "作品" },
  { key: "characters", label: "角色" },
];

const hotKeywords = ["星海回响", "治愈", "剧场版", "角色分析", "国创"];

const includesKeyword = (keyword: string, values: string[]) =>
  values.some((value) => value.toLowerCase().includes(keyword.toLowerCase()));

export default function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const query = (params.get("q") || "").trim();
  const [term, setTerm] = useState(query);
  const [tab, setTab] = useState<TabKey>("all");

  useEffect(() => {
    setTerm(query);
    setTab("all");
  }, [query]);

  useSeo(
    query ? `搜索「${query}」- age动漫站内搜索` : "站内搜索 - age动漫",
    query
      ? `age动漫站内搜索「${query}」的结果，包含相关资讯、动画作品与角色分析内容。`
      : "在age动漫搜索动漫资讯、作品与角色分析，输入关键词即可快速找到相关内容。",
    "age动漫搜索,动漫搜索,资讯搜索,作品搜索,角色搜索",
  );

  const matchedNews = useMemo(
    () =>
      query
        ? newsList.filter((item) => includesKeyword(query, [item.title, item.summary, item.tag, item.content.join(" ")]))
        : [],
    [query],
  );
  const matchedWorks = useMemo(
    () =>
      query
        ? worksList.filter((item) =>
            includesKeyword(query, [item.title, item.originalTitle, item.summary, item.studio, item.type, ...item.tags]),
          )
        : [],
    [query],
  );
  const matchedCharacters = useMemo(
    () =>
      query
        ? charactersList.filter((item) =>
            includesKeyword(query, [item.name, item.workTitle, item.summary, item.archetype, ...item.traits]),
          )
        : [],
    [query],
  );

  const total = matchedNews.length + matchedWorks.length + matchedCharacters.length;

  const counts: Record<TabKey, number> = {
    all: total,
    news: matchedNews.length,
    works: matchedWorks.length,
    characters: matchedCharacters.length,
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = term.trim();
    navigate(next ? `/search?q=${encodeURIComponent(next)}` : "/search");
  };

  const showNews = tab === "all" || tab === "news";
  const showWorks = tab === "all" || tab === "works";
  const showCharacters = tab === "all" || tab === "characters";
  const recommendations = [...worksList].sort((a, b) => b.favoriteCount - a.favoriteCount).slice(0, 3);

  return (
    <PageShell>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-6 lg:px-10 py-6 md:py-9">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
          <div className="mt-4 max-w-3xl">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-5 h-[3px] rounded-full bg-primary-500"></span>
              <span className="text-[11px] md:text-xs font-medium tracking-[0.16em] uppercase text-primary-600">
                SEARCH
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">站内搜索</h1>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
              {query ? (
                <>
                  当前搜索词：<span className="text-foreground-900 font-medium">「{query}」</span>
                  ，共找到 {total} 条相关内容。
                </>
              ) : (
                "输入关键词，搜索 age动漫 的资讯、作品与角色分析内容。"
              )}
            </p>

            <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2" role="search">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  value={term}
                  onChange={(event) => setTerm(event.target.value)}
                  placeholder="试试搜索「星海回响」「治愈」「剧场版」"
                  aria-label="搜索关键词"
                  className="w-full h-11 rounded-md border border-background-300 bg-background-50 pl-10 pr-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <button
                type="submit"
                className="h-11 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-foreground-400">热门搜索</span>
              {hotKeywords.map((keyword) => (
                <Link
                  key={keyword}
                  to={`/search?q=${encodeURIComponent(keyword)}`}
                  className="text-[11px] px-2 py-1 rounded bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  {keyword}
                </Link>
              ))}
              <span className="text-[11px] text-foreground-400 ml-auto flex items-center gap-1">
                <i className="ri-information-line"></i>
                仅演示搜索，数据有限
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 lg:px-10 py-6 md:py-10">
        {query && total > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 mb-6">
            {tabs.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                aria-pressed={tab === item.key}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  tab === item.key
                    ? "bg-primary-500 text-background-50 font-medium"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {item.label}
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    tab === item.key ? "bg-background-50/25 text-background-50" : "bg-background-50 text-foreground-500"
                  }`}
                >
                  {counts[item.key]}
                </span>
              </button>
            ))}
          </div>
        )}

        {!query ? (
          <div className="max-w-3xl">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-3">
                <i className="ri-search-line text-xl"></i>
              </div>
              <h2 className="text-base font-semibold text-foreground-900">输入关键词开始搜索</h2>
              <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                支持搜索资讯标题与摘要、作品名称与标签、角色名称与所属作品。也可以直接点击上方的热门搜索。
              </p>
            </div>
            <h2 className="mt-8 mb-4 text-base font-semibold text-foreground-950">大家都在看</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {recommendations.map((item) => (
                <WorkCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        ) : total === 0 ? (
          <div className="max-w-3xl">
            <EmptyState
              title="没有找到相关内容"
              description={`没有与「${query}」匹配的资讯、作品或角色。可以换个关键词，或者看看下面的推荐内容。`}
              icon="ri-search-eye-line"
              action={
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Link
                    to="/search"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line"></i>
                    重置搜索
                  </Link>
                  <Link
                    to="/works"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    浏览作品库
                  </Link>
                </div>
              }
            />
            <h2 className="mt-8 mb-4 text-base font-semibold text-foreground-950">相关推荐</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {recommendations.map((item) => (
                <WorkCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-8 md:gap-10">
            {showNews && matchedNews.length > 0 && (
              <section aria-labelledby="search-news-heading">
                <div className="flex items-end justify-between gap-3 mb-4">
                  <h2 id="search-news-heading" className="text-base md:text-lg font-semibold text-foreground-950">
                    资讯 <span className="text-sm font-normal text-foreground-400">({matchedNews.length})</span>
                  </h2>
                  {tab === "all" && matchedNews.length > 3 && (
                    <button
                      type="button"
                      onClick={() => setTab("news")}
                      className="text-xs text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      查看全部
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(tab === "all" ? matchedNews.slice(0, 4) : matchedNews).map((item) => (
                    <NewsCard key={item.id} item={item} variant="list" />
                  ))}
                </div>
              </section>
            )}

            {showWorks && matchedWorks.length > 0 && (
              <section aria-labelledby="search-works-heading">
                <div className="flex items-end justify-between gap-3 mb-4">
                  <h2 id="search-works-heading" className="text-base md:text-lg font-semibold text-foreground-950">
                    作品 <span className="text-sm font-normal text-foreground-400">({matchedWorks.length})</span>
                  </h2>
                  {tab === "all" && matchedWorks.length > 4 && (
                    <button
                      type="button"
                      onClick={() => setTab("works")}
                      className="text-xs text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      查看全部
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {(tab === "all" ? matchedWorks.slice(0, 4) : matchedWorks).map((item) => (
                    <WorkCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            )}

            {showCharacters && matchedCharacters.length > 0 && (
              <section aria-labelledby="search-characters-heading">
                <div className="flex items-end justify-between gap-3 mb-4">
                  <h2 id="search-characters-heading" className="text-base md:text-lg font-semibold text-foreground-950">
                    角色 <span className="text-sm font-normal text-foreground-400">({matchedCharacters.length})</span>
                  </h2>
                  {tab === "all" && matchedCharacters.length > 3 && (
                    <button
                      type="button"
                      onClick={() => setTab("characters")}
                      className="text-xs text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      查看全部
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                  {(tab === "all" ? matchedCharacters.slice(0, 3) : matchedCharacters).map((item) => (
                    <CharacterCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            )}

            <p className="text-[11px] text-foreground-400 flex items-center gap-1.5">
              <i className="ri-information-line"></i>
              搜索结果基于本地演示数据，仅演示搜索能力，数据有限。
            </p>
          </div>
        )}
      </div>
    </PageShell>
  );
}